import { addLineItems, checkout, getCart, getCartError, updateLineItem } from '@/cart/cart-store'
import type { Cart, CartLineItem } from '@/cart/types'
import '@/components/buy-stack/TrialImageCarouselRegister'
import '@/components/social-proof/SocialProofRegister'
import Experiment from '@/feature-flags/experiment'
import { getLocalizedStrings } from '@/localization/localization-store'
import withRootRoute from '@/localization/with-root-route'
import { effect } from '@preact/signals'

function main() {
  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput)
  }

  if (!customElements.get('bs-10-buy-stack')) {
    customElements.define('bs-10-buy-stack', Bs10BuyStack)
  }
}

class Bs10BuyStack extends HTMLElement {
  private inputs: QuantityInput[]
  private sellingPlanId: number | null
  private isSubscription = true

  /**
   * Set of variant ids used in this buy-stack
   */
  private variantIds = new Set<number>()

  /**
   * Reference to the current cart state
   */
  private cart: Cart | null

  /**
   * Error returned by the latest cart operation, or null if it was successful
   */
  private cartError: string | null

  /**
   * Input mapping from variant id to cart item,
   * useful for quickly looking up the item quantity, for example.
   * Onetimes are mapped to index 0, subscriptions to index 1
   */
  private items = [new Map<string, CartLineItem>(), new Map<string, CartLineItem>()]

  constructor() {
    super()

    this.sellingPlanId = Number(this.getAttribute('selling-plan-id')) || null
    this.isSubscription = this.sellingPlanId !== null

    this.querySelector('select[name="select-selling-plan"]')?.addEventListener('change', e =>
      this.handleSelectSellingPlan(e)
    )

    const inputs: NodeListOf<QuantityInput> = document.querySelectorAll(
      'quantity-input[data-variant-id]'
    )
    this.inputs = Array.from(inputs)

    for (const el of this.inputs) {
      el.addEventListener('increment', () => this.handleIncrement(el))
      el.addEventListener('decrement', () => this.handleDecrement(el))
      this.variantIds.add(Number(el.dataset.variantId!))
    }

    this.querySelector('button[name="add-to-cart"]')?.addEventListener('click', e => {
      e.preventDefault()
      const url = Experiment.getInstance().getFlag('precheckout_page')
        ? '/pages/precheckout'
        : '/checkout'
      const rootRoute = getLocalizedStrings().storeRootRoute
      void checkout(withRootRoute(url, rootRoute))
    })

    const a = this.querySelector('a[name="toggle-subscription"]')
    a?.addEventListener('click', e => {
      e.preventDefault()
      this.handleToggleSubscription()
    })

    // Sync internal state with global cart state
    effect(() => {
      this.cart = getCart()

      if (!this.cart) return

      this.items.forEach(map => map.clear())

      for (const item of this.cart.items) {
        const isSub = Number(!!item.selling_plan_allocation)
        this.items[isSub].set(item.id.toString(), item)
      }

      this.build()
    })

    effect(() => {
      this.cartError = getCartError()
      this.build()
    })
  }

  /**
   * Update each subscription item in the cart to the new selling plan.
   * Ignore onetimes and items that aren't variants in this buy-stack.
   * @param e - the selling plan select element
   */
  handleSelectSellingPlan(e: Event) {
    const el = e.currentTarget as HTMLSelectElement
    this.sellingPlanId = Number(el.value)

    if (!this.cart || !this.isSubscription) return

    for (let i = 0; i < this.cart.items.length; i++) {
      const item = this.cart.items[i]

      if (!item.selling_plan_allocation || !this.variantIds.has(item.id)) {
        continue
      }

      void updateLineItem({
        line: i + 1,
        quantity: item.quantity,
        properties: item.properties ?? undefined,
        selling_plan: this.sellingPlanId
      })
    }
  }

  /**
   * Add to cart
   * @param el - the quantity input for the variant
   */
  handleIncrement(el: QuantityInput) {
    const item = {
      id: Number(el.dataset.variantId!),
      quantity: 1,
      selling_plan: this.isSubscription ? (this.sellingPlanId ?? undefined) : undefined
    }

    void addLineItems([item])
  }

  /**
   * Find the first matching line item in the cart and update its quantity.
   *
   * @param el - the quantity input for the variant
   */
  handleDecrement(el: QuantityInput) {
    if (!this.cart) return

    for (let i = 0; i < this.cart.items.length; i++) {
      const item = this.cart.items[i]

      if (this.isSubscription !== !!item.selling_plan_allocation) {
        continue
      }

      if (item.id.toString() !== el.dataset.variantId) {
        continue
      }

      void updateLineItem({
        line: i + 1,
        quantity: el.value,
        properties: item.properties ?? undefined,
        selling_plan: this.isSubscription ? (this.sellingPlanId ?? undefined) : undefined
      })
    }
  }

  handleToggleSubscription() {
    this.isSubscription = !this.isSubscription
    this.build()

    this.querySelector('social-proof')?.scrollIntoView({
      behavior: 'smooth'
    })
  }

  build() {
    const isSub = Number(this.isSubscription)

    isSub ? this.setAttribute('subscription', '') : this.removeAttribute('subscription')

    const items = this.items[isSub]
    const ctaDisabled = items.size === 0

    ctaDisabled ? this.setAttribute('cta-disabled', '') : this.removeAttribute('cta-disabled')

    for (const el of this.inputs) {
      const id = el.dataset.variantId!
      const item = items.get(id)
      el.value = item?.quantity ?? 0
    }

    // cart error message
    const errorContainer = this.querySelector('#cart-error')
    const errorSpan = errorContainer?.getElementsByTagName('span')[0]
    if (errorContainer && errorSpan) {
      errorContainer.classList.toggle('opacity-0', !this.cartError)
      errorContainer.classList.toggle('max-h-0', !this.cartError)
      errorContainer.classList.toggle('max-h-12', !!this.cartError)
      errorContainer.classList.toggle('pointer-events-none', !this.cartError)

      if (this.cartError) {
        errorSpan.textContent = this.cartError
        errorSpan.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else {
        errorContainer.addEventListener('transitionend', () => {
          errorSpan.textContent = this.cartError
        })
      }
    }
  }
}

class QuantityInput extends HTMLElement {
  static observedAttributes = ['value', 'max', 'min']

  _value = 0
  minValue = 0

  constructor() {
    super()

    this.value = this.hasAttribute('value') ? Number(this.getAttribute('value')) : 0

    this.querySelectorAll('button[name="increment"]').forEach(el =>
      el.addEventListener('click', () => {
        this.value++
        this.dispatchEvent(new CustomEvent('increment'))
      })
    )

    this.querySelectorAll('button[name="decrement"]').forEach(el =>
      el.addEventListener('click', () => {
        this.value--
        this.dispatchEvent(new CustomEvent('decrement'))
      })
    )

    this.build()
  }

  get value() {
    return this._value
  }

  set value(value: number) {
    const newValue = Math.max(this.minValue, value)

    if (newValue === this._value) {
      return
    }

    this._value = newValue
    this.build()
    this.dispatchEvent(new CustomEvent('update:value', { detail: { value: this.value } }))
  }

  build() {
    this.setAttribute('value', this.value.toString())
    this.querySelector('span[name="quantity-value"]')?.replaceChildren(this.value.toString())
  }
}

main()
